/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import React, { FunctionComponent, useCallback, useEffect } from 'react';

import { EpisodeInfo } from 'client-code/episode-guide';

import { TiVoMemo } from '../../../core/tivo/tivo-react-wrapper';
import { RootState, useTivoDispatch, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { ListTypes } from '../../../core/utils';
import {
    selectIsAnyPickerGhostActive,
    setLastFocusedType,
} from '../../../store/seasonpicker/seasonpicker.reducers';
import { Mousetrap } from '../../common';
import { ProgressBar } from '../../common/ProgressBar';
import { AllEpisodesListUtility } from './AllEpisodesListUtility';

import './AllEpisodesListItem.scss';

interface AllEpisodesListItemProps {
    episode: EpisodeInfo;
    itemClass: string;
    index: number;
    isFocused: boolean;
    ghostMode?: boolean;
}

const selectAnySeasonPickerFocused = (state: RootState): boolean => {
    const pickers = state.seasonPicker.pickers || {};
    return Object.values(pickers).some((picker: any) => picker && picker.isFocused);
};

const AllEpisodesListItemInner: FunctionComponent<AllEpisodesListItemProps> = ({
    episode,
    itemClass,
    index,
    isFocused,
    ghostMode,
}): JSX.Element => {
    const dispatch = useTivoDispatch();
    const lastFocusedType = useTivoSelector(
        (state: RootState) => state.seasonPicker.lastFocusedType,
    );
    const anySeasonPickerFocused = useTivoSelector(selectAnySeasonPickerFocused);
    const isMainListFocused = useTivoSelector(
        (state: RootState) => state.list[ListTypes.ALL_EPISODES_LIST]?.isFocused ?? false,
    );

    const isAnyPickerGhostActive = useTivoSelector(
        useCallback(
            (state: RootState) =>
                selectIsAnyPickerGhostActive(state, ghostMode ?? false, isMainListFocused),
            [ghostMode, isMainListFocused],
        ),
    );

    useEffect(() => {
        if (isFocused) {
            dispatch(setLastFocusedType('episode'));
        }
    }, [isFocused, dispatch]);

    const showGhost =
        isFocused && ghostMode && !anySeasonPickerFocused && lastFocusedType === 'episode';

    const showHighlight = isFocused && !anySeasonPickerFocused && !isAnyPickerGhostActive;
    const episodeClasses = `ae-episode-item ${showHighlight ? itemClass : ''} ${showGhost ? 'ghostHighlight' : ''}`;

    return (
        <div key={`episode-container-${episode.contentId}`} className="ae-episode-container">
            <Mousetrap
                key={`episode-${episode.contentId}`}
                value={`${index}_${ListTypes.ALL_EPISODES_LIST}`}
                className={episodeClasses}
            >
                <div className="ae-episode-info">
                    <span className="ae-episode-number">
                        {AllEpisodesListUtility.formatEpisodeNumber(
                            episode.seasonOrYear,
                            episode.episodeNum,
                        )}
                    </span>
                    <span className="ae-episode-title">&lsquo;{episode.title}&rsquo;</span>
                </div>
                {episode.percentWatched !== undefined && episode.percentWatched > 0 && (
                    <div className="ae-progress-bar">
                        <ProgressBar percentage={episode.percentWatched} />
                    </div>
                )}
            </Mousetrap>
        </div>
    );
};

const AllEpisodesListItem = TiVoMemo(AllEpisodesListItemInner);

export default AllEpisodesListItem;
